import PropTypes from 'prop-types';
import { Popover, Box } from '@mui/material';
import { CustomSelectPanel, CustomDatePanel, CustomTextPanel, CustomMoneyPanel, CustomNumberPanel } from './index';
import { updateFilters, GetGridKeys } from '../dataGridHelper';

const getFilterOperator = (column, defaultOperators, fallback) => {
  const { customFilterOperators, filterOperators } = column;
  const preferredOperator = defaultOperators.find(op => customFilterOperators?.includes(op) && filterOperators?.some(fo => fo.value === op));
  return preferredOperator || filterOperators?.find(operator => customFilterOperators?.includes(operator.value))?.value || fallback;
};

CustomFilter.propTypes = {
  gridId: PropTypes.string,
  anchorEl: PropTypes.object,
  setAnchorEl: PropTypes.func,
  setActiveFilter: PropTypes.func,
  activeFilter: PropTypes.string,
  filters: PropTypes.object,
  setFilters: PropTypes.func,
  fieldValues: PropTypes.object,
  columns: PropTypes.array,
  maxValue: PropTypes.object,
};

export default function CustomFilter({
  gridId,
  anchorEl,
  setAnchorEl,
  activeFilter,
  setActiveFilter,
  columns,
  filters,
  setFilters,
  maxValue,
  fieldValues,
}) {
  const { filterStorageKey } = GetGridKeys(gridId);

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => {
        setAnchorEl(null);
        setActiveFilter(null);
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      sx={{
        ml: -8,
        '& .MuiPopover-paper': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: 'auto',
          minWidth: '16px',
          maxWidth: '320px',
          minHeight: '16px',
          maxHeight: '300px',
          padding: '8px 0',
          borderRadius: '4px',
          bgcolor: 'background.paper',
          boxShadow:
            '0 5px 5px -3px rgba(145, 158, 171, 0.20), 0 8px 10px 1px rgba(145, 158, 171, 0.14), 0 3px 14px 2px rgba(145, 158, 171, 0.12)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none !important',
          },
        },
      }}
    >
      {activeFilter && (
        <Box sx={{ px: '4px' }}>
          {(() => {
            const column = columns.find((col) => col.field === activeFilter);
            switch (column?.type) {
              case 'customNumber':
                return (
                  <CustomNumberPanel
                    activeFilter={activeFilter}
                    filters={filters}
                    setFilters={(newFilters) => updateFilters(setFilters, filterStorageKey, newFilters)}
                  />
                );
              case 'customMoney':
                return (
                  <Box sx={{ width: '260px', px: '4px' }}>
                    <CustomMoneyPanel
                      activeFilter={activeFilter}
                      filters={filters}
                      setFilters={(newFilters) => updateFilters(setFilters, filterStorageKey, newFilters)}
                      maxValue={maxValue?.[activeFilter] || 10000}
                    />
                  </Box>
                );
              case 'customText':
                return <Box sx={{ width: '225px', px: '4px' }}>
                  <CustomTextPanel
                    activeFilter={activeFilter}
                    filters={filters}
                    setFilters={(newFilters) => updateFilters(setFilters, filterStorageKey, newFilters)}
                  />
                </Box>;
              case 'customDate':
                return <Box sx={{ width: '320px', px: '4px' }}>
                  <CustomDatePanel
                    activeFilter={activeFilter}
                    filters={filters}
                    setFilters={(newFilters) => updateFilters(setFilters, filterStorageKey, newFilters)}
                  />
                </Box>;
              default:
                return <Box sx={{ maxHeight: '300px', height: 'auto' }}>
                  <CustomSelectPanel
                    item={{ field: activeFilter, value: filters[activeFilter]?.value }}
                    applyValue={({ value }) => {
                      const updatedFilters = { ...filters, [activeFilter]: value };
                      updateFilters(setFilters, filterStorageKey, updatedFilters);
                    }}
                    updateFreeTextFilter={(search) => {
                      const newFilters = search
                        ? { ...filters, [activeFilter]: { value: search, operator: getFilterOperator(column, ['containsAnySubstring', 'contains'], 'contains') } }
                        : { ...filters };
                      setFilters(newFilters);
                    }}
                    values={fieldValues[activeFilter] || []}
                    appliedFilters={filters[activeFilter]?.value && Array.isArray(filters[activeFilter]?.value) ? filters[activeFilter]?.value : []}
                    onFilterChange={(newValue) => {
                      const newFilters = {
                        ...filters,
                        [activeFilter]: {
                          value: newValue.map((v) => v.value),
                          operator: getFilterOperator(column, ['isAnyOf', 'hasAnyOf'], 'isAnyOf')
                        },
                      };
                      updateFilters(setFilters, filterStorageKey, newFilters);
                    }}
                    gridId={gridId}
                  />
                </Box>;
            }
          })()}
        </Box>
      )}
    </Popover>
  );
}